import React from 'react';
import { useUserStore } from '../../stores/userStore';
import { userAsyncActions } from '../../stores/actions/userActions';
import { userKeys } from '../query/userQuery';
import { useUserQuery, useUserMarksQuery } from '../query/userQuery';
import { useAsyncOperations } from './asyncCommon';
import { bffAuthService } from '../../services/BffAuthService';

export { userKeys };

// Store-based hooks for direct store access
export const useUserStoreData = () => {
  const currentUser = useUserStore((state) => state.currentUser);
  const users = useUserStore((state) => state.users);
  const userMarks = useUserStore((state) => state.userMarks);
  const status = useUserStore((state) => state.status);
  
  return {
    currentUser,
    users,
    userMarks,
    status
  };
};

// Store actions hook
export const useUserStoreActions = () => {
  const setCurrentUser = useUserStore((state) => state.setCurrentUser);
  const setStatus = useUserStore((state) => state.setStatus);
  const setUserMarks = useUserStore((state) => state.setUserMarks);
  
  return React.useMemo(() => ({
    setCurrentUser,
    setStatus,
    setUserMarks
  }), [setCurrentUser, setStatus, setUserMarks]);
};

// 综合管理hook，结合React Query和Zustand
export const useUserManager = () => {
  const storeData = useUserStoreData();
  const storeActions = useUserStoreActions();
  const { safeAsyncOperation, queryClient } = useAsyncOperations();
  
  // 使用actions中的异步方法
  const { fetchUserAsync, fetchUserMarksAsync } = userAsyncActions;
  
  const { 
    data: userData, 
    isLoading: userLoading, 
    error: userError 
  } = useUserQuery();
  
 
  const { 
    data: userMarksData, 
    isLoading: userMarksLoading, 
    error: userMarksError 
  } = useUserMarksQuery([]);
  
  // 数据同步逻辑：将React Query数据同步到Zustand store
  React.useEffect(() => {
    if (userData && !userLoading) {
      const currentStoreData = useUserStore.getState().currentUser;
      const needsEnrichment = !currentStoreData || !currentStoreData.userId;
      
      if (needsEnrichment) {
        const enrichWithUserId = async () => {
          let enrichedUserData = currentStoreData || userData;
          if (!enrichedUserData.userId) {
            console.log('=== useUserManager: 补充 userId 到用户数据 ===');

            let userInfo = bffAuthService.getUserInfo();
            if (!userInfo) {
              userInfo = await bffAuthService.fetchUserInfo();
            }

            if (userInfo && userInfo.sub) {
              enrichedUserData = {
                ...enrichedUserData,
                userId: String(userInfo.sub)
              };
              console.log('补充后的 userData (已添加 userId):', enrichedUserData.userId);
            } else {
              console.warn('⚠️ 无法从 userInfo 获取 userId，尝试使用 _id');
              if (enrichedUserData._id) {
                enrichedUserData = {
                  ...enrichedUserData,
                  userId: String(enrichedUserData._id)
                };
                console.log('使用 _id 作为 userId:', enrichedUserData.userId);
              }
            }
          }

          storeActions.setCurrentUser(enrichedUserData);
          storeActions.setStatus('idle');
        };

        enrichWithUserId();
      }
    }
  }, [userData, userLoading]);
  
  // 用户标记数据同步
  React.useEffect(() => {
    if (userMarksData && !userMarksLoading) {
      // 只有当store中无数据时才用React Query数据初始化store，避免覆盖乐观更新
      const currentStoreData = useUserStore.getState().userMarks;
      if (currentStoreData.length === 0) {
        storeActions.setUserMarks(userMarksData);
      }
    }
  }, [userMarksData, userMarksLoading]);
  
  // 智能错误处理
  const hasError = userError || userMarksError;
  
  // Integration actions实现 - 使用统一异步操作封装
  const updateUserWithSync = async (user: any) => {
    const result = await safeAsyncOperation(async () => {
      await fetchUserAsync();
      queryClient.invalidateQueries({ queryKey: userKeys.detail(user.userId) });
      return { success: true };
    }, '更新用户失败');
    
    return result.success;
  };
  
  const syncAllUserData = async () => {
    const result = await safeAsyncOperation(async () => {
      await queryClient.refetchQueries({ queryKey: userKeys.detail('current') });
      await queryClient.refetchQueries({ queryKey: userKeys.list() });
      await queryClient.refetchQueries({ queryKey: userKeys.marks() });
      return { success: true };
    }, '同步所有用户数据失败');
    
    return result.success;
  };
  
  return {
    // Store状态
    ...storeData,
    
    // Loading states
    isLoading: userLoading || userMarksLoading,
    userLoading,
    userMarksLoading,
    
    // Error states
    error: hasError,
    userError,
    userMarksError,
    
    // Actions操作
    fetchUserAsync,
    fetchUserMarksAsync,
    
    // Integration actions
    updateUserWithSync,
    syncAllData: syncAllUserData,
    
    // 同步方法（直接暴露给组件使用）
    fetchUserMarks: fetchUserMarksAsync,
    
    // Combined state
    isUserLoggedIn: !!storeData.currentUser,
    username: storeData.currentUser?.nickName || storeData.currentUser?.name || '',
    hasUsers: storeData.users.length > 0,
    hasUserMarks: storeData.userMarks.length > 0,
    isUserLoading: userLoading || userMarksLoading || storeData.status === 'loading',
    
    // 智能状态判断
    canCreateUser: !userLoading && !hasError,
    canEditUser: !!storeData.currentUser && !userLoading && !hasError,
  };
};